import { useEffect, useState } from "react";
import { type WebhookDef, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const BUILT_IN_ACTIONS = [
  { value: "lock_computer", label: "Lock Computer" },
  { value: "run_script", label: "Run Script" },
  { value: "push_notification", label: "Push Notification" },
  { value: "open_url", label: "Open URL" },
];

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleString();
}

// ── Create form ───────────────────────────────────────────────────────────────

function CreateForm({ syncKey, onCreated }: { syncKey: string; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [action, setAction] = useState("lock_computer");
  const [customAction, setCustomAction] = useState("");
  const [target, setTarget] = useState("desktop");
  const [priority, setPriority] = useState(5);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isCustom = action === "__custom__";
  const resolvedAction = isCustom ? customAction.trim() : action;

  async function submit() {
    if (!name.trim() || !resolvedAction) return;
    setError("");
    setBusy(true);
    try {
      await api.webhooks.create(syncKey, {
        name: name.trim(),
        action: resolvedAction,
        target,
        priority,
        secret: secret.trim() || undefined,
      });
      setName("");
      setSecret("");
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-section">
      <h2>New webhook</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Name</label>
          <input
            placeholder="GitHub push"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} style={{ width: "100%" }}>
            {BUILT_IN_ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {isCustom && (
            <input
              placeholder="custom.action_name"
              value={customAction}
              onChange={(e) => setCustomAction(e.target.value)}
              style={{ width: "100%", marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
            />
          )}
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Target</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: "100%" }}>
            <option value="desktop">desktop</option>
            <option value="server">server</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
            Priority <span style={{ color: "#555" }}>(1=high, 10=low)</span>
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
            Secret <span style={{ color: "#555" }}>(optional HMAC-SHA256)</span>
          </label>
          <input
            type="password"
            placeholder="shared secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
          />
        </div>
      </div>
      {error && <p style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{error}</p>}
      <button type="button" onClick={submit} disabled={busy || !name.trim() || !resolvedAction}>
        {busy ? "Creating…" : "Create Webhook"}
      </button>
    </div>
  );
}

// ── Webhook row ───────────────────────────────────────────────────────────────

function WebhookRow({ hook, syncKey, onReload }: { hook: WebhookDef; syncKey: string; onReload: () => void }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const triggerUrl = `${BASE}/webhooks/${hook.id}/trigger`;

  function copyUrl() {
    navigator.clipboard.writeText(triggerUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); } catch { /* ignore */ } finally { setBusy(false); onReload(); }
  }

  return (
    <div className="settings-section" style={{ marginBottom: 8, padding: "10px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: hook.enabled ? "#34d399" : "#71717a",
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1, fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>{hook.name}</span>
        <code style={{ fontSize: 11, color: "#94a3b8" }}>{hook.action}</code>
        <span style={{ fontSize: 11, color: "#555" }}>
          {hook.triggerCount} trigger{hook.triggerCount !== 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={copyUrl}
          style={{ fontSize: 11, padding: "2px 10px", minWidth: 60 }}
        >
          {copied ? "Copied!" : "Copy URL"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act(() => api.webhooks.toggle(syncKey, hook.id, !hook.enabled))}
          style={{ fontSize: 11, padding: "2px 10px" }}
        >
          {hook.enabled ? "Disable" : "Enable"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act(() => api.webhooks.delete(syncKey, hook.id))}
          className="clip-icon-btn"
          title="Delete"
        >
          🗑
        </button>
      </div>
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <code
          style={{
            flex: 1,
            fontSize: 11,
            color: "#475569",
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "4px 8px",
            background: "rgba(0,0,0,0.3)",
            borderRadius: 4,
          }}
        >
          POST {triggerUrl}
        </code>
        {hook.secret && (
          <span style={{ fontSize: 10, color: "#60a5fa" }}>🔒 HMAC</span>
        )}
        {hook.lastTriggeredAt && (
          <span style={{ fontSize: 11, color: "#555", flexShrink: 0 }}>
            last: {fmtDate(hook.lastTriggeredAt)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Webhooks({ syncKey }: Props) {
  const [hooks, setHooks] = useState<WebhookDef[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.webhooks.list(syncKey);
      setHooks(res.webhooks);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page">
      <h1>Webhooks</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
        Inbound HTTP endpoints. Any service can <code>POST</code> to the trigger URL to enqueue
        an action. Incoming body is merged into the action params. Optionally protect with
        an HMAC-SHA256 shared secret — send the signature as{" "}
        <code>X-Webhook-Signature: sha256=&lt;hex&gt;</code>.
      </p>

      <CreateForm syncKey={syncKey} onCreated={load} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 10px" }}>
        <h2 style={{ margin: 0 }}>Webhooks ({hooks.length})</h2>
        <button type="button" onClick={load} style={{ marginLeft: "auto", fontSize: 11, padding: "2px 10px" }}>
          Refresh
        </button>
      </div>

      {loading && hooks.length === 0 ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : hooks.length === 0 ? (
        <p className="empty">No webhooks yet.</p>
      ) : (
        hooks.map((h) => <WebhookRow key={h.id} hook={h} syncKey={syncKey} onReload={load} />)
      )}
    </div>
  );
}
